import { ImageSourcePropType, ImageURISource } from 'react-native';

import { UserV2 } from '@/services/getUserV2';

type AvatarUrl = string & ImageURISource;

type Avatar =
  | {
      Location128px?: string;
      Bucket?: string;
      ETag?: string;
      Key?: string;
      Location?: string;
      ServerSideEncryption?: string;
      isPublic?: boolean;
    }
  | null
  | undefined
  | UserV2['avatar'];

/**
 * Normalizes S3 URLs to avoid SSL certificate issues with dot-containing bucket names.
 *
 * @todo Fix root cause: Migrate image serving to CloudFront or rename S3 buckets to avoid dots.
 * Current buckets like 'dev-primum.co-images' cause ERR_CERT_COMMON_NAME_INVALID errors
 * when accessed via virtual-hosted style URLs over HTTPS. This client-side workaround
 * converts them to path-style URLs as a temporary mitigation.
 *
 * @param url - The S3 URL to normalize
 * @returns Normalized URL or original URL if normalization fails
 */
function normalizeS3Url(url: string): string {
  try {
    const parsed = new URL(url);

    const hostParts = parsed.hostname.split('.');

    const s3Index = hostParts.findIndex((part) => part === 's3');

    if (s3Index > 0 && hostParts.length >= s3Index + 3) {
      const bucket = hostParts.slice(0, s3Index).join('.');

      const region = hostParts[s3Index + 1];

      if (bucket.includes('.')) {
        parsed.hostname = `s3.${region}.amazonaws.com`;

        parsed.pathname = `/${bucket}${parsed.pathname}`;

        return parsed.toString();
      }
    }

    return url;
  } catch {
    return url;
  }
}

/**
 * Width asked of the CDN for a large avatar: a 128pt profile header at 3x.
 * Small avatars use `Location128px`, which the API sets to 256px.
 */
const LARGE_AVATAR_WIDTH = 384;

/**
 * The avatar to draw. `Location128px` suits anything up to about 128pt.
 * `large` asks the CDN for a bigger copy when the avatar is served from it,
 * which a CDN-built `Location128px` signals by its width parameter; an
 * avatar from before the CDN falls back to its stored image.
 */
function selectAvatarUrl(avatar: Avatar, large: boolean) {
  if (large && avatar?.Location && avatar.Location128px?.includes('w=')) {
    return `${avatar.Location}?w=${LARGE_AVATAR_WIDTH}`;
  }

  return avatar?.Location128px ?? avatar?.Location;
}

export function getAvatarUrl(avatar?: Avatar, formatForUri = false, { large = false } = {}) {
  const avatarUrl = selectAvatarUrl(avatar, large);

  if (!avatarUrl || typeof avatarUrl !== 'string') {
    return undefined;
  }

  const normalized = normalizeS3Url(avatarUrl);

  if (formatForUri) {
    return { uri: normalized as AvatarUrl } as string & ImageSourcePropType;
  }

  return normalized as AvatarUrl;
}
